import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiRobotOutline } from '@mdi/js'
import type { AgentMission, AgentRun } from '../../api/schema'
import { useLocale } from '../../contexts/LocaleContext'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { AGENT_RUN_STATUS, displayStatus, toneVariant } from './agent-run-status'
import { proposalBuckets } from './proposal-buckets'

type Decision = 'approve' | 'reject' | 'cancel'

export interface AgentRunBannerProps {
    org: string
    run: AgentRun
    mission?: AgentMission
    /** data.write: approve, reject, cancel, relaunch. */
    canDecide: boolean
    /** data.write or a platform admin: the details link. */
    canOpenDetails: boolean
    onDecision?: (decision: Decision) => Promise<string | undefined>
    /** Opens the launch dialog; the button shows only with it. */
    onRelaunch?: () => void
}

/**
 * The latest agent run of a target as one bordered row: a pending-proposal
 * summary with the decisions, the progress of an active run, or the status
 * of a finished one. Presentational.
 */
export function AgentRunBanner({ org, run, mission, canDecide, canOpenDetails, onDecision, onRelaunch }: AgentRunBannerProps) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const [confirm, setConfirm] = useState<Decision | null>(null)
    const [busy, setBusy] = useState(false)
    const [refusal, setRefusal] = useState<string | undefined>()

    const status = displayStatus(run)
    const cfg = AGENT_RUN_STATUS[status]
    const active = run.status === 'queued' || run.status === 'running'
    const pending = run.pending_proposals
    const title = mission ? t(mission.title_key, { defaultValue: mission.code }) : run.mission_code
    const step = run.current_step

    const decide = async (decision: Decision) => {
        if (!onDecision) return
        setBusy(true)
        setRefusal(undefined)
        try {
            setRefusal(await onDecision(decision))
        } finally {
            setBusy(false)
            setConfirm(null)
        }
    }

    const confirmText =
        confirm === 'approve'
            ? t('card.confirm_approve', { count: pending.total })
            : confirm === 'reject'
              ? t('card.confirm_reject', { count: pending.total })
              : t('card.confirm_cancel')

    let summary
    if (active) {
        summary = (
            <>
                <span className="font-medium text-foreground">{title}</span>
                <Badge variant={toneVariant(cfg.tone)} data-testid="agent-run-status">{t(cfg.labelKey)}</Badge>
                <span className="text-muted-foreground" data-testid="agent-run-step">
                    {run.status === 'queued'
                        ? t('card.waiting')
                        : step
                          ? `${step.position > 0 ? `${step.position}/${step.steps_total} · ` : ''}${step.title_key ? t(step.title_key, { defaultValue: step.code }) : step.code}`
                          : t('card.starting')}
                </span>
            </>
        )
    } else if (pending.total > 0) {
        const parts = proposalBuckets(pending).map((b) => t(`banner.buckets.${b.bucket}`, { count: b.count }))
        summary = (
            <span className="text-foreground" data-testid="agent-banner-pending">
                {t('banner.pending', { title, parts: parts.join(', ') })}
            </span>
        )
    } else {
        summary = (
            <>
                <span className="font-medium text-foreground">{title}</span>
                <Badge variant={toneVariant(cfg.tone)} data-testid="agent-run-status">{t(cfg.labelKey)}</Badge>
                <span className="text-muted-foreground">{new Date(run.created_at).toLocaleString(locale)}</span>
            </>
        )
    }

    return (
        <div
            className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-border bg-card px-3 py-2 text-sm"
            data-testid="agent-runs-banner"
        >
            <AppIcon path={mdiRobotOutline} size="sm" className="text-muted-foreground" />
            {summary}
            <div className="ml-auto flex flex-wrap items-center gap-2">
                {canOpenDetails && (
                    <Button size="sm" variant="ghost" asChild data-testid="agent-banner-details">
                        <Link to="/$org/agent-runs/$runUid" params={{ org, runUid: run.uid }}>
                            {t('banner.details')}
                        </Link>
                    </Button>
                )}
                {canDecide && !active && pending.total > 0 && (
                    <>
                        <Button size="sm" onClick={() => setConfirm('approve')} disabled={busy} data-testid="agent-banner-approve">
                            {t('banner.approve')}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirm('reject')} disabled={busy} data-testid="agent-banner-reject">
                            {t('banner.reject')}
                        </Button>
                    </>
                )}
                {canDecide && active && (
                    <Button size="sm" variant="outline" onClick={() => setConfirm('cancel')} disabled={busy || status === 'cancelling'} data-testid="agent-banner-cancel">
                        {t('banner.cancel')}
                    </Button>
                )}
                {canDecide && !active && pending.total === 0 && onRelaunch && (
                    <Button size="sm" variant="outline" onClick={onRelaunch} data-testid="agent-banner-relaunch">
                        {t('banner.relaunch')}
                    </Button>
                )}
            </div>
            {refusal && (
                <div className="basis-full text-destructive" data-testid="agent-run-decision-error">{refusal}</div>
            )}

            <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
                <AlertDialogContent data-testid="agent-run-decision-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {confirm === 'approve' ? t('card.approve_all') : confirm === 'reject' ? t('card.reject_all') : t('card.cancel')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{confirmText}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="agent-run-decision-dismiss">{t('card.keep')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault()
                                if (confirm) void decide(confirm)
                            }}
                            disabled={busy}
                            data-testid="agent-run-decision-submit"
                        >
                            {t('card.confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
